import React, { useEffect, useMemo, useState } from 'react';
import {
    CAvatar,
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCol,
    CFormInput,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilLayers, cilPencil, cilPlus, cilSearch } from '@coreui/icons';
import { useNavigate } from 'react-router-dom';
import * as DomicilappService from '../../services/DomicilappService';
import { useStore } from '../../contexts/StoreContext';
import { ComboListItem } from '../../types/combo';

export const formatCOP = (value: number) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(value || 0);

// Ej. "2× Hamburguesa clásica, 1× Gaseosa (1.5L)"
export const comboItemsSummary = (combo: ComboListItem) =>
    combo.items
        .map((item) => `${item.quantity}× ${item.product_name}${item.variant_name ? ` (${item.variant_name})` : ''}`)
        .join(', ');

const ComboList: React.FC = () => {
    const navigate = useNavigate();
    const { storeId } = useStore();
    const [combos, setCombos] = useState<ComboListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!storeId) return;
        setCombos([]);
        setLoading(true);
        DomicilappService.getCombosByStore(storeId)
            .then((response) => setCombos(response?.status === 200 ? response.data?.items ?? [] : []))
            .catch(() => setCombos([]))
            .finally(() => setLoading(false));
    }, [storeId]);

    const filteredCombos = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return combos;
        return combos.filter((c) => c.name.toLowerCase().includes(term) || comboItemsSummary(c).toLowerCase().includes(term));
    }, [combos, searchTerm]);

    const activeCount = combos.filter((c) => c.is_active).length;

    return (
        <div className="combo-list-page">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="fw-bold text-body mb-1">Combos</h3>
                    <p className="text-body-secondary small mb-0">Agrupa productos de tu tienda con un precio especial</p>
                </div>
                <CButton color="dark" className="d-flex align-items-center gap-2" onClick={() => navigate('/combos/create')}>
                    <CIcon icon={cilPlus} size="sm" />
                    <span>Nuevo combo</span>
                </CButton>
            </div>

            <CRow className="g-3 mb-4">
                <CCol sm={4}>
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-3">
                            <span className="text-uppercase text-body-secondary fw-bold" style={{ fontSize: '0.7rem' }}>Total combos</span>
                            <h4 className="fw-bold text-body mb-0 mt-1">{combos.length}</h4>
                        </CCardBody>
                    </CCard>
                </CCol>
                <CCol sm={4}>
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-3">
                            <span className="text-uppercase text-body-secondary fw-bold" style={{ fontSize: '0.7rem' }}>Publicados</span>
                            <h4 className="fw-bold text-success mb-0 mt-1">{activeCount}</h4>
                        </CCardBody>
                    </CCard>
                </CCol>
                <CCol sm={4}>
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-3">
                            <span className="text-uppercase text-body-secondary fw-bold" style={{ fontSize: '0.7rem' }}>Ocultos</span>
                            <h4 className="fw-bold text-body-secondary mb-0 mt-1">{combos.length - activeCount}</h4>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>

            <CCard className="border-0 shadow-sm rounded-3">
                <CCardBody className="p-4">
                    <div className="position-relative mb-4" style={{ maxWidth: '420px' }}>
                        <CFormInput
                            placeholder="Buscar por nombre o producto..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="ps-5"
                        />
                        <CIcon icon={cilSearch} className="position-absolute text-body-secondary" style={{ left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                    </div>

                    <CTable responsive align="middle" hover className="border-top">
                        <CTableHead>
                            <CTableRow>
                                <CTableHeaderCell style={{ width: '60px' }}>Imagen</CTableHeaderCell>
                                <CTableHeaderCell>Combo</CTableHeaderCell>
                                <CTableHeaderCell>Estado</CTableHeaderCell>
                                <CTableHeaderCell>Precio</CTableHeaderCell>
                                <CTableHeaderCell className="text-end">Acciones</CTableHeaderCell>
                            </CTableRow>
                        </CTableHead>
                        <CTableBody>
                            {loading ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={5} className="text-center py-5">
                                        <CSpinner size="sm" className="me-2" />
                                        <span className="text-body-secondary">Cargando combos...</span>
                                    </CTableDataCell>
                                </CTableRow>
                            ) : filteredCombos.length === 0 ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={5} className="text-center py-5 text-body-secondary">
                                        {combos.length === 0 ? 'Aún no tienes combos. Crea el primero con "Nuevo combo".' : 'Ningún combo coincide con la búsqueda.'}
                                    </CTableDataCell>
                                </CTableRow>
                            ) : (
                                filteredCombos.map((combo) => {
                                    const savings = combo.regular_price - combo.price;
                                    const hasUnavailable = combo.items.some((item) => !item.is_available);
                                    return (
                                        <CTableRow key={combo.id} className="cursor-pointer" onClick={() => navigate(`/combos/edit/${combo.id}`)}>
                                            <CTableDataCell>
                                                {combo.image ? (
                                                    <CAvatar src={combo.image} shape="rounded" size="md" />
                                                ) : (
                                                    <div className="rounded d-flex align-items-center justify-content-center text-body-secondary bg-body-tertiary" style={{ width: '40px', height: '40px' }}>
                                                        <CIcon icon={cilLayers} />
                                                    </div>
                                                )}
                                            </CTableDataCell>
                                            <CTableDataCell>
                                                <div className="fw-bold text-body">{combo.name}</div>
                                                <small className="text-body-secondary d-block text-truncate" style={{ maxWidth: '380px' }}>
                                                    {comboItemsSummary(combo)}
                                                </small>
                                                {hasUnavailable && (
                                                    <small className="text-warning">Incluye un producto agotado</small>
                                                )}
                                            </CTableDataCell>
                                            <CTableDataCell>
                                                {combo.is_active ? (
                                                    <CBadge color="success-subtle" className="text-success border border-success-subtle px-2 py-1">● Publicado</CBadge>
                                                ) : (
                                                    <CBadge color="secondary-subtle" className="text-secondary border px-2 py-1">● Oculto</CBadge>
                                                )}
                                            </CTableDataCell>
                                            <CTableDataCell>
                                                <div className="fw-bold text-body">{formatCOP(combo.price)}</div>
                                                {savings > 0 && (
                                                    <small className="text-body-secondary">
                                                        <s>{formatCOP(combo.regular_price)}</s> · ahorra {formatCOP(savings)}
                                                    </small>
                                                )}
                                            </CTableDataCell>
                                            <CTableDataCell className="text-end">
                                                <CButton color="light" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/combos/edit/${combo.id}`); }}>
                                                    <CIcon icon={cilPencil} size="sm" className="me-1" /> Editar
                                                </CButton>
                                            </CTableDataCell>
                                        </CTableRow>
                                    );
                                })
                            )}
                        </CTableBody>
                    </CTable>
                </CCardBody>
            </CCard>
        </div>
    );
};

export default ComboList;
